<template>
  <div class="news-page">
    <!-- 头部导航 - 使用公共组件 -->
    <Navbar />

    <!-- Hero Section -->
    <section class="hero-section" :style="{ backgroundImage: `url(${heroBackground})` }">
      <div class="hero-overlay"></div>
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">新闻资讯</h1>
          <p class="hero-subtitle">关注行业动态，了解检测资讯</p>
          <p class="hero-description">
            提供工业安全检测行业最新资讯、政策法规解读、技术标准更新，以及公司动态和项目进展
          </p>
        </div>
      </div>
    </section>

    <!-- 新闻内容区 -->
    <section class="news-content-section">
      <div class="container">
        <div class="content-wrapper">
          <!-- 左侧：新闻列表 -->
          <div class="news-list-container">
            <!-- 分类标签 -->
            <div class="category-tabs">
              <button 
                v-for="category in categories" 
                :key="category.id"
                :class="['category-tab', { active: currentCategory === category.id }]"
                @click="changeCategory(category.id)"
              >
                {{ category.name }}
              </button>
            </div>

            <!-- 加载中 -->
            <div v-if="loading" class="loading-container">
              <i class="el-icon-loading"></i>
              <p>加载中...</p>
            </div>

            <!-- 新闻列表 -->
            <div class="news-list" v-else>
              <div 
                v-for="news in displayedNews" 
                :key="news.id"
                class="news-item"
                @click="viewNewsDetail(news.id)"
              >
                <div class="news-image">
                  <img :src="news.image" :alt="news.title" />
                  <span class="news-badge" :class="news.badge.type">{{ news.badge.text }}</span>
                </div>
                <div class="news-content">
                  <h3 class="news-title">{{ news.title }}</h3>
                  <p class="news-excerpt">{{ news.excerpt }}</p>
                  <div class="news-meta">
                    <span class="meta-item">
                      <i class="el-icon-time"></i>
                      {{ news.date }}
                    </span>
                    <span class="meta-item">
                      <i class="el-icon-view"></i>
                      {{ news.views }} 阅读
                    </span>
                    <span class="meta-item">
                      <i class="el-icon-folder-opened"></i>
                      {{ news.category }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 分页 -->
            <div class="pagination" v-if="total > 0">
              <el-pagination
                background
                layout="total, prev, pager, next"
                :total="total"
                :page-size="pageSize"
                :current-page.sync="currentPage"
                @current-change="handlePageChange"
              >
              </el-pagination>
            </div>
            
            <!-- 空状态 -->
            <div v-if="!loading && newsList.length === 0" class="empty-state">
              <i class="el-icon-document"></i>
              <p>暂无新闻</p>
            </div>
          </div>

          <!-- 右侧：侧边栏 -->
          <aside class="news-sidebar">
            <!-- 搜索框 -->
            <div class="sidebar-widget search-widget">
              <el-input
                v-model="searchKeyword"
                placeholder="搜索新闻..."
                prefix-icon="el-icon-search"
                @keyup.enter.native="handleSearch"
              >
                <el-button slot="append" icon="el-icon-search" @click="handleSearch"></el-button>
              </el-input>
            </div>

            <!-- 热门新闻 -->
            <div class="sidebar-widget">
              <h3 class="widget-title">热门新闻</h3>
              <div class="hot-news-list">
                <div 
                  v-for="(news, index) in hotNews" 
                  :key="news.id"
                  class="hot-news-item"
                  @click="viewNewsDetail(news.id)"
                >
                  <span class="hot-rank" :class="{ top: index < 3 }">{{ index + 1 }}</span>
                  <div class="hot-news-content">
                    <h4 class="hot-news-title">{{ news.title }}</h4>
                    <p class="hot-news-date">{{ news.date }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 最新公告 -->
            <div class="sidebar-widget notice-widget">
              <h3 class="widget-title">最新公告</h3>
              <div class="notice-list">
                <div 
                  v-for="notice in latestNotices" 
                  :key="notice.id"
                  class="notice-item"
                  @click="viewNewsDetail(notice.id)"
                >
                  <div class="notice-title">{{ notice.title }}</div>
                  <div class="notice-date">{{ notice.date }}</div>
                </div>
                <div v-if="latestNotices.length === 0" class="notice-empty">
                  暂无公告
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <!-- 底部区域 - 使用公共组件 -->
    <Footer />
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'

export default {
  name: 'News',
  components: {
    Navbar,
    Footer
  },
  data() {
    return {
      // 临时使用在线图片，建议后续替换为本地图片以提高性能
      heroBackground: require('@/assets/news.jpg'),
      searchKeyword: '',
      currentCategory: 'all',
      currentPage: 1,
      pageSize: 6,
      categories: [],
      categoriesMap: {},
      newsList: [],
      total: 0,
      loading: false,
      oldNewsList: [
        {
          id: 9,
          title: '陕西省检验检测机构资质认定自我声明公开承诺书',
          excerpt: '本机构就提交的资质认定申报事项自我声明公开承诺，确保申请材料真实、合法、有效，符合相关法律法规要求，自觉接受社会监督。',
          image: require('@/assets/chengnuoshu.png'),
          category: '公示公告',
          categoryId: 'company',
          date: '2025-10-15',
          views: 156,
          badge: { text: '最新', type: 'hot' }
        },
        {
          id: 1,
          title: '新国标实施对工业检测的影响与应对措施',
          excerpt: '2025年最新国家标准正式生效，对企业安全检测提出更高要求。新规定明确了各类安全设备的检测周期和标准，有助于提升整体工业安全水平。',
          image: require('@/assets/news1.jpg'),
          category: '行业动态',
          categoryId: 'industry',
          date: '2025-09-20',
          views: 1245,
          badge: { text: '热点', type: 'hot' }
        },
        {
          id: 2,
          title: '慧安检测荣获2025年度行业创新奖',
          excerpt: '凭借先进的检测技术和优质服务，公司荣获2025年度行业创新奖。这一荣誉充分肯定了我们在技术创新和服务质量方面的卓越表现。',
          image: require('@/assets/news2.jpg'),
          category: '公示公告',
          categoryId: 'company',
          date: '2025-09-15',
          views: 2138,
          badge: { text: '推荐', type: 'recommend' }
        },
        {
          id: 3,
          title: '安全检测技术培训班圆满结束',
          excerpt: '为期三天的安全检测技术培训班成功举办，吸引了百余名专业人士参与。培训内容涵盖最新检测技术、标准解读和实操演练。',
          image: require('@/assets/news3.jpg'),
          category: '公示公告',
          categoryId: 'company',
          date: '2025-09-10',
          views: 892,
          badge: { text: '活动', type: 'event' }
        },
        {
          id: 4,
          title: '工业安全设备检测新技术研讨会召开',
          excerpt: '行业专家齐聚一堂，共同探讨工业安全检测领域的最新技术与发展趋势。会议就智能检测、大数据应用等前沿技术进行了深入交流。',
          image: require('@/assets/banner1.png'),
          category: '行业动态',
          categoryId: 'industry',
          date: '2025-09-05',
          views: 1567,
          badge: { text: '', type: '' }
        },
        {
          id: 5,
          title: '气瓶检测周期调整通知',
          excerpt: '根据最新规范要求，部分气瓶检测周期有所调整，请各单位注意。详细调整内容请查看完整公告。',
          image: require('@/assets/banner2.jpg'),
          category: '政策法规',
          categoryId: 'policy',
          date: '2025-08-30',
          views: 3421,
          badge: { text: '重要', type: 'important' }
        },
        {
          id: 6,
          title: '呼吸器检测常见问题解答',
          excerpt: '针对客户常见的呼吸器检测问题，我们整理了详细的解答手册，帮助企业更好地了解检测流程和注意事项。',
          image: require('@/assets/banner3.jpg'),
          category: '技术标准',
          categoryId: 'technology',
          date: '2025-08-25',
          views: 2890,
          badge: { text: '', type: '' }
        },
        {
          id: 7,
          title: '智能检测设备投入使用，提升检测效率',
          excerpt: '公司引进最新智能检测设备，采用AI技术辅助检测，大幅提升了检测精度和效率，为客户提供更优质的服务。',
          image: require('@/assets/case1.jpg'),
          category: '公示公告',
          categoryId: 'company',
          date: '2025-08-20',
          views: 1876,
          badge: { text: '', type: '' }
        },
        {
          id: 8,
          title: '特种设备安全法修订解读',
          excerpt: '新修订的特种设备安全法将于2026年1月1日起施行，本文对主要修订内容进行解读，帮助企业及时了解新规要求。',
          image: require('@/assets/case2.jpg'),
          category: '政策法规',
          categoryId: 'policy',
          date: '2025-08-15',
          views: 4532,
          badge: { text: '热点', type: 'hot' }
        }
      ]
    }
  },
  computed: {
    filteredNews() {
      return this.newsList
    },
    displayedNews() {
      return this.newsList
    },
    hotNews() {
      return [...this.newsList]
        .sort((a, b) => b.views - a.views)
        .slice(0, 5)
    },
    // 最新公告（公示公告分类的新闻）
    latestNotices() {
      // 从categoriesMap中找到"公示公告"的ID
      const noticesCategoryId = Object.keys(this.categoriesMap).find(
        id => this.categoriesMap[id] === '公示公告'
      )
      
      if (!noticesCategoryId) return []
      
      // 筛选出公示公告分类的新闻，按时间排序，取前5条
      return [...this.newsList]
        .filter(news => news.categoryId == noticesCategoryId)
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 5)
    }
  },
  methods: {
    // 获取新闻分类
    async fetchCategories() {
      try {
        const response = await this.$axios.get('/api/news/categories')
        if (response.data.code === 200) {
          const apiCategories = response.data.data || []
          // 构建分类映射
          this.categoriesMap = {}
          apiCategories.forEach(cat => {
            this.categoriesMap[cat.id] = cat.name
          })
          
          // 构建分类列表
          this.categories = [
            { id: 'all', name: '全部' },
            ...apiCategories.map(cat => ({
              id: cat.id,
              name: cat.name
            }))
          ]
        }
      } catch (error) {
        console.error('获取分类失败:', error)
      }
    },
    
    // 获取新闻列表
    async fetchNews() {
      try {
        this.loading = true
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          status: 1 // 只获取已发布的新闻
        }
        
        // 如果选择了分类，添加分类筛选
        if (this.currentCategory !== 'all') {
          params.category_id = this.currentCategory
        }
        
        // 如果有搜索关键词，添加关键词筛选
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        
        const response = await this.$axios.get('/api/news', { params })
        
        if (response.data.code === 200) {
          const result = response.data.data
          this.total = result.total
          
          // 转换数据格式
          this.newsList = result.list.map(news => ({
            id: news.id,
            title: news.title,
            excerpt: news.summary || '',
            image: news.image_url || require('@/assets/news1.jpg'),
            category: news.category_name || '',
            categoryId: news.category_id,
            date: this.formatDate(news.created_at),
            views: news.views || 0,
            badge: this.getBadge(news)
          }))
        }
      } catch (error) {
        console.error('获取新闻列表失败:', error)
        this.$message.error('获取新闻列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 获取徽章
    getBadge(news) {
      // 不显示徽章
      return { text: '', type: '' }
    },
    
    changeCategory(categoryId) {
      if (this.currentCategory === categoryId) {
        return // 如果点击的是当前分类，不做任何操作
      }
      this.currentCategory = categoryId
      this.currentPage = 1 // 切换分类时重置到第一页
      this.fetchNews()
    },
    
    handlePageChange(page) {
      this.currentPage = page
      this.fetchNews()
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    
    handleSearch() {
      this.currentPage = 1
      this.currentCategory = 'all'
      this.fetchNews()
    },
    
    viewNewsDetail(newsId) {
      // 跳转到新闻详情页
      this.$router.push(`/news/${newsId}`)
    }
  },
  
  async mounted() {
    // 页面加载完成后的操作
    document.title = '新闻资讯 - 慧安检测'
    
    // 获取分类和新闻
    await this.fetchCategories()
    await this.fetchNews()
  }
}
</script>

<style scoped>
.news-page {
  min-height: 100vh;
  background: #f7f8fa;
}

/* Hero Section */
.hero-section {
  height: 470px;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.1) 100%);
  z-index: 1;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
  z-index: 2;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 3;
}

.hero-content {
  text-align: center;
  color: white;
  position: relative;
  z-index: 3;
}

.hero-title {
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 16px 0;
  animation: fadeInUp 0.8s ease-out;
}

.hero-subtitle {
  font-size: 24px;
  margin: 0 0 16px 0;
  opacity: 0.95;
  animation: fadeInUp 0.8s ease-out 0.1s both;
}

.hero-description {
  font-size: 16px;
  line-height: 1.8;
  max-width: 800px;
  margin: 0 auto;
  opacity: 0.9;
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 新闻内容区 */
.news-content-section {
  padding: 60px 0;
}

.content-wrapper {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 40px;
}

/* 新闻列表 */
.news-list-container {
  background: transparent;
}

/* 分类标签 */
.category-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
  flex-wrap: wrap;
}

.category-tab {
  padding: 10px 24px;
  background: white;
  border: 2px solid #e8e8e8;
  border-radius: 25px;
  font-size: 15px;
  color: #666666;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
}

.category-tab:hover {
  border-color: #1e3a8a;
  color: #1e3a8a;
  transform: translateY(-2px);
}

.category-tab.active {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  border-color: transparent;
}

/* 新闻列表 */
.news-list {
  display: grid;
  gap: 24px;
  margin-bottom: 40px;
}

.news-item {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 24px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  transition: all 0.3s;
  cursor: pointer;
}

.news-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.news-image {
  width: 280px;
  height: 200px;
  overflow: hidden;
  position: relative;
  background: #f5f5f5;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.news-item:hover .news-image img {
  transform: scale(1.08);
}

.news-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  z-index: 1;
}

.news-badge.hot {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.news-badge.recommend {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
}

.news-badge.important {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
}

.news-badge.event {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.news-content {
  padding: 20px 24px 20px 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.news-title {
  font-size: 20px;
  font-weight: 600;
  color: #333333;
  margin: 0 0 12px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-excerpt {
  font-size: 14px;
  line-height: 1.7;
  color: #666666;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-meta {
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #999999;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.meta-item i {
  font-size: 14px;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: center;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 64px;
  margin-bottom: 16px;
  display: block;
}

.empty-state p {
  font-size: 16px;
  margin: 0;
}

/* 加载状态 */
.loading-container {
  text-align: center;
  padding: 80px 20px;
  color: #409EFF;
}

.loading-container i {
  font-size: 48px;
  margin-bottom: 16px;
  display: block;
  animation: rotating 2s linear infinite;
}

.loading-container p {
  font-size: 16px;
  margin: 0;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* 侧边栏 */
.news-sidebar {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sidebar-widget {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.widget-title {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
  position: relative;
}

.widget-title::before {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 40px;
  height: 2px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
}

/* 搜索框 */
.search-widget {
  padding: 20px;
}

/* 热门新闻 */
.hot-news-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hot-news-item {
  display: flex;
  gap: 12px;
  cursor: pointer;
  transition: all 0.3s;
  padding: 8px;
  border-radius: 6px;
}

.hot-news-item:hover {
  background: #f7f8fa;
}

.hot-rank {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background: #e8e8e8;
  color: #666666;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
}

.hot-rank.top {
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
}

.hot-news-content {
  flex: 1;
  min-width: 0;
}

.hot-news-title {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
  margin: 0 0 6px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hot-news-date {
  font-size: 12px;
  color: #999999;
  margin: 0;
}

/* 公告 */
.notice-widget {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border: 1px solid rgba(102, 126, 234, 0.15);
}

.notice-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.notice-item {
  padding: 12px;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid #f0f0f0;
}

.notice-item:hover {
  background: #f7f8fa;
  border-color: #1e3a8a;
  transform: translateX(4px);
}

.notice-title {
  font-size: 14px;
  color: #333333;
  line-height: 1.5;
  margin-bottom: 6px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notice-date {
  font-size: 12px;
  color: #999999;
}

.notice-empty {
  text-align: center;
  padding: 20px;
  color: #999999;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .content-wrapper {
    grid-template-columns: 1fr 320px;
    gap: 32px;
  }
}

@media (max-width: 992px) {
  .hero-section {
    height: 470px;
  }

  .hero-title {
    font-size: 36px;
  }

  .hero-subtitle {
    font-size: 20px;
  }

  .content-wrapper {
    grid-template-columns: 1fr;
    gap: 40px;
  }

  .news-item {
    grid-template-columns: 240px 1fr;
    gap: 20px;
  }

  .news-image {
    width: 240px;
    height: 180px;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: auto;
    min-height: 300px;
    padding: 80px 0 40px;
    background-position: center top;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 18px;
  }

  .hero-description {
    font-size: 14px;
  }

  .news-content-section {
    padding: 40px 0;
  }

  .category-tabs {
    gap: 8px;
    margin-bottom: 24px;
  }

  .category-tab {
    padding: 8px 16px;
    font-size: 14px;
  }

  .news-item {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .news-image {
    width: 100%;
    height: 200px;
  }

  .news-content {
    padding: 20px;
  }

  .news-title {
    font-size: 18px;
  }

  .news-meta {
    gap: 16px;
    flex-wrap: wrap;
  }
}
</style>

